import { Button, Checkbox, CircularProgress, FormControl, FormControlLabel, Grid, IconButton, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { PhotoCamera, Replay } from '@mui/icons-material';
import React, {SyntheticEvent, useEffect, useState} from 'react';
import { uploadFile, getFile } from '../../services';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';
import { useUpdateEffect, usePrevious } from '../../hooks';
import styled from 'styled-components';
import { Check } from 'react-feather';
import Modal from './modal';
import LogoSherwood from '../../img/favicon-96x96.png';
import PDFLogo from '../../img/pdf_logo.jpeg';
import { isImageType } from '../../utils/index.jsx';
import { CloseFrame } from './mini_components';

enum UPLOAD_STATE{
    NOT_UPLOAD = 0,
    LOADING = 1,
    OK = 2,
    ERROR = 3,
}

interface PostFile{
    file:string, 
    file_type:string
}
interface FileUpload{
    image?:FileList, 
    buffer?:{data:ArrayBufferLike, type:string},
    type:string,
    status:UPLOAD_STATE, 
    remoteName?:string
}
interface Props extends LocalizeContextProps{
    initialState : {listFiles:FileUpload[]},
    label:string,
    mode:string,
    value:{file:string, file_type : string}[],
    imagesSelected : (images : PostFile[]) => void
}

const OpacityLayer = styled.div`
    position:absolute;
    width:100%;
    height:100%;
    background-color:black;
    opacity:0.2;
`;

const StatusLayer = styled.div`
    position:absolute;
    width:100%;
    height:100%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index:1000;
`;

const GridImage = styled(Grid)`
    display:inline-block;
    position:relative;
    align-items: center;
    justify-content: center;
    padding:0.5rem;
    max-height:10rem;
    max-width: 10rem;
    overflow: hidden;
`;

const ImageFile = styled.img`
    max-width:100%;
    display:block;
    height:auto;
    cursor:pointer;
`;

/**
 * Compresses an image file by resizing and reducing quality
 * @param file - The original image file
 * @param maxWidth - Maximum width in pixels (default: 1920)
 * @param maxHeight - Maximum height in pixels (default: 1920)
 * @param quality - JPEG quality 0-1 (default: 0.8)
 * @returns Promise<File> - The compressed file
 */
async function compressImage(file: File, maxWidth: number = 1920, maxHeight: number = 1920, quality: number = 0.8): Promise<File> {
    return new Promise((resolve, reject) => {
        // Only compress image files
        if (!file.type.startsWith('image/')) {
            resolve(file);
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                // Calculate new dimensions
                if (width > maxWidth || height > maxHeight) {
                    const ratio = Math.min(maxWidth / width, maxHeight / height);
                    width = width * ratio;
                    height = height * ratio;
                }

                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Could not get canvas context'));
                    return;
                }

                // Draw and compress
                ctx.drawImage(img, 0, 0, width, height);
                
                // Convert to JPEG for better compression (maintains original type if supported)
                const outputType = file.type === 'image/png' ? 'image/jpeg' : file.type;
                
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            reject(new Error('Compression failed'));
                            return;
                        }
                        // Create a new File with the compressed blob
                        // Use window.File to avoid conflict with component name
                        const compressedFile = new window.File([blob], file.name, {
                            type: outputType,
                            lastModified: Date.now(),
                        });
                        resolve(compressedFile);
                    },
                    outputType,
                    quality
                );
            };
            img.onerror = () => reject(new Error('Failed to load image'));
            img.src = e.target?.result as string;
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
}

const File:React.FC<Props> = (props) => {
    const [filesSelected, setFilesSelected] = useState<FileUpload[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [localImage, setLocalImage] = useState("");
    const prevFilesSelected:FileUpload[] | undefined = usePrevious(filesSelected);
    const prevValue:FileUpload[] | undefined = usePrevious(props.value);
    const [addingFile, setAddingFile] = useState(false);

    function dataToImageUrl(data:ArrayBufferLike){
        const uint8Array = new Uint8Array(data);

        // Create a Blob from the Uint8Array with the appropriate MIME type
        const blob = new Blob([uint8Array], { type: 'image/jpeg' }); // Adjust the type as needed
        
        return URL.createObjectURL(blob);
    }
    function removeFile(index:number){
        let newFilesSelected = [...filesSelected];
        newFilesSelected.splice(index, 1);
        setFilesSelected(newFilesSelected);
        let remoteNames:PostFile[] = [];
        for(let i = 0; i < newFilesSelected.length; i++){
            const file = newFilesSelected[i];
            const element:PostFile = {
                file:file.remoteName as string,
                file_type : file.type
            }
            remoteNames.push(element);
        }
        
        props.imagesSelected(remoteNames);
    }
    function showFullSize(index:number){
        const file = filesSelected[index];
        if(file.buffer){            
            setLocalImage(dataToImageUrl(file.buffer.data));
            setShowModal(true);
        }
        
    }
    function downloadPDF(indexPDF:number){
        // SHER-197 — open PDF inline in a new tab instead of forcing a download.
        // The browser's native PDF viewer still exposes a Download button so
        // users can save when they want to, but we no longer dump every
        // clinical PDF into the local Downloads folder by default.
        const dataBuffer = filesSelected[indexPDF].buffer;
        if(dataBuffer){
            const arr = new Uint8Array(dataBuffer.data);
            const blob = new Blob([arr], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const newWindow = window.open(url, '_blank');
            const remoteName = filesSelected[indexPDF].remoteName as string | undefined;
            if (newWindow && remoteName) {
                newWindow.document.title = remoteName;
            }
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        }
    }
    function renderFileStatus(status:number, index:number){
        switch(status){
            case UPLOAD_STATE.LOADING:
                return (
                    <StatusLayer>
                        <CircularProgress color="inherit" />
                    </StatusLayer>
                );
            case UPLOAD_STATE.OK:
                return (
                    <StatusLayer>
                        <Check color="green" style={{ width: 60, height: 60}} />
                    </StatusLayer>
                );
            default:
                return (
                    <StatusLayer>
                        <Replay color="error" onClick={() => uploadAndUpdate(index)} />
                    </StatusLayer>)
        }
    }
    async function onFileSelected(e:React.ChangeEvent<HTMLInputElement>){
        if(e !== null && e.target.files !== null && e.target.files.length > 0){
            const value = e.target.files;
            console.log("File selected");
            let tempFilesSelected = [...filesSelected];
            
            // Compress image if it's an image file
            let fileToUpload = value[0];
            if (value[0].type.startsWith('image/')) {
                try {
                    const originalSize = value[0].size;
                    fileToUpload = await compressImage(value[0]);
                    const compressedSize = fileToUpload.size;
                    const reduction = ((1 - compressedSize / originalSize) * 100).toFixed(1);
                    console.log(`Image compression: ${(originalSize / 1024 / 1024).toFixed(2)}MB → ${(compressedSize / 1024 / 1024).toFixed(2)}MB (${reduction}% reduction)`);
                } catch (error) {
                    console.error('Image compression failed:', error);
                    // Fall back to original file if compression fails
                    fileToUpload = value[0];
                }
            }
            
            // Create a new FileList with the (possibly compressed) file
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(fileToUpload);
            const processedFileList = dataTransfer.files;
            
            tempFilesSelected.push({
                image: processedFileList, 
                status: UPLOAD_STATE.LOADING, 
                type: fileToUpload.type
            });
            console.log(tempFilesSelected);
            setAddingFile(true);
            setFilesSelected(tempFilesSelected);
            
        }
    }
    async function getFileIndex(index:number){
        try{
            const file = filesSelected[index];
            const response = await getFile(file.remoteName);
            return response;
        }
        catch(err){
            console.log(err);
            return false;
        }
    }
    async function uploadFileIndex(index:number){
        try{
            const file = filesSelected[index];
            if(file.image){
                const response = await uploadFile(file.image[0]);
                return response;
            }
            else{
                return false;
            }

        }
        catch(err){
            console.log(err);
            return false;
        }
    }
    async function uploadAndUpdate(lastIndex:number){
        let tempFilesSelected = [...filesSelected];

        const response = await uploadFileIndex(lastIndex);

        if(response){
            tempFilesSelected[lastIndex].status = UPLOAD_STATE.OK;
            tempFilesSelected[lastIndex].remoteName = response.fileName as string;
            let remoteNames:PostFile[] = [];
            for(let i = 0; i < tempFilesSelected.length; i++){
                const file = tempFilesSelected[i];
                const element:PostFile = {
                    file:file.remoteName as string,
                    file_type : file.type
                }
                remoteNames.push(element);
            }

            props.imagesSelected(remoteNames);
        }
        else{
            tempFilesSelected[lastIndex].status = UPLOAD_STATE.ERROR;
        }
        setFilesSelected(tempFilesSelected);
    }
    async function getAndUpdate(index:number){
        let tempFilesSelected = [...filesSelected];

        const response = await getFileIndex(index);
        
        if(response){
            tempFilesSelected[index].status = UPLOAD_STATE.OK;
            tempFilesSelected[index].buffer = response.file.Body
        }
        else{
            tempFilesSelected[index].status = UPLOAD_STATE.ERROR;
        }
        setFilesSelected(tempFilesSelected);
    }
    useEffect(()=>{
        async function changeFiles(){
            if((props.value && props.value.length > 0) && (filesSelected.length === props.value.length) && !addingFile){
                // si es el mount inicial, me traigo los archivos
                for(let i = 0; i < filesSelected.length; i++){
                    if(!((filesSelected[i].status === UPLOAD_STATE.OK) || (filesSelected[i].status === UPLOAD_STATE.ERROR))){
                        await getAndUpdate(i);
                    }
                } 
            }
            else if(prevFilesSelected){
                    if(prevFilesSelected.length < filesSelected.length && addingFile){
                        //Mando la imagen al servidor
                        const lastIndex = filesSelected.length -1;
                        await uploadAndUpdate(lastIndex);
                        setAddingFile(false);
                    }
                    else if(prevFilesSelected.length > filesSelected.length){
                        //Borro la imagen que falte
                    }
                }
            }
        
        changeFiles();
    }, [filesSelected]);
    useEffect(() =>{
        async function loadFiles(){
            if(typeof prevValue === "undefined" && props.value && props.value.length > 0 && (props.mode === "show" || filesSelected.length === 0)){
                const tempFiles:FileUpload[] = props.value.map(file => {
                    return {
                        remoteName:file.file,
                        status:UPLOAD_STATE.LOADING,
                        type:file.file_type
                    }
                })
                setFilesSelected(tempFiles);
            }
        }
        loadFiles();
    }, [props.value]);
    
    const hasUploadError = filesSelected.some(f => f.status === UPLOAD_STATE.ERROR);

    return (
        <Grid container>
            <Modal
                open={showModal}
                closeModal={() => setShowModal(false)}
                >
                <ImageFile src={localImage} width="100%" alt="Logo"/>
            </Modal>
            <Grid item xs={12}>
                <Typography variant="body2" gutterBottom>
                    {props.label}
                </Typography>
                {hasUploadError && (
                    <Typography variant="body2" color="error" gutterBottom>
                        <Translate id="hospital.file-upload-error" />
                    </Typography>
                )}
            </Grid>
            <Grid className='files_container' item container direction={'row'}  spacing={3} xs={12}>
                {
                    filesSelected.map((file, index) =>{
                        if(file.image){
                            if(file.type === "image/png" || file.type === "image/jpeg" || file.type === "image/jpg"){
                                return(
                                    <CloseFrame hide={props.mode === "show"} onClick={() => removeFile(index)}>
                                        <GridImage item>
                                            {
                                                renderFileStatus(file.status, index)
                                            }
                                            <OpacityLayer />
                                            
                                            <ImageFile src={URL.createObjectURL(file.image[0])} alt="imagen" />
                                        </GridImage>
                                    </CloseFrame>) 
                            }
                            else{
                                return(
                                    <CloseFrame hide={props.mode === "show"} onClick={() => removeFile(index)}>
                                        <GridImage>
                                            {
                                                renderFileStatus(file.status, index)
                                            }
                                            <OpacityLayer />
                                            
                                            <ImageFile src={PDFLogo} alt="pdf" />
                                        </GridImage>
                                    </CloseFrame>) 
                            }
                             
                        }
                        if(file.buffer){
                            if(isImageType(file.type)){
                                const dataUrl = dataToImageUrl(file.buffer.data)

                                // Create a data URL
                                //const dataUrl = `data:image/jpeg;base64,${base64String}`; // Change the format as needed
                            
                                return(
                                    <CloseFrame hide={props.mode === "show"} onClick={() => removeFile(index)}>
                                        <GridImage item xs={2}>
                                            <ImageFile onClick={() => showFullSize(index)} src={dataUrl} alt=""/>
                                        </GridImage>
                                    </CloseFrame>
                                )
                            }
                            else{
                                return(
                                    <CloseFrame hide={props.mode === "show"} onClick={() => removeFile(index)}>
                                        <GridImage item xs={2}>
                                            <ImageFile onClick={() => downloadPDF(index)}  src={PDFLogo} alt="pdf" />
                                        </GridImage>
                                    </CloseFrame>
                                )
                            }
                        }
                        else{
                            return(
                            
                                    <GridImage item xs={2}>
                                        {
                                            renderFileStatus(file.status, index)
                                        }
                                        <OpacityLayer />
                                        <ImageFile src={LogoSherwood} width="100%" alt="Logo"/>
                                    </GridImage>
                            
                            )
                        }
                        
                    })
                }
                {
                    props.mode === "form" &&
                    
                    <Grid item xs={2}>
                        <input accept="image/*,application/pdf,application/vnd.ms-excel" id="image" name="image" style={{display:'none'}} 
                            type="file" 
                            onChange={(e) => onFileSelected(e)} />
                        <label htmlFor="image">
                            <IconButton color="secondary" aria-label="upload picture" component="span" size="large">
                                <PhotoCamera />
                            </IconButton>
                        </label>
                    </Grid>
                }
                
            </Grid>
            
        </Grid>
    );
}

export default withLocalize(File);